import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import './AppShell.css';

/**
 * The persistent frame for dashboard, learn, practice, live-lab,
 * playbook, and team. Lesson, roleplay, and debrief routes live
 * OUTSIDE this shell because they ship their own top bars (lesson
 * progress / scenario badge + timer / debrief header).
 *
 * Ported from the Comcast Sales Academy deployment with the brand
 * mark made generic (no cable-tv association). The "useLearner" hook
 * is stubbed here in Phase A — when the polished Dashboard screen
 * lands in Phase B it'll be replaced with the real learnerStore.
 */

const BRAND_FAN_COLORS = [
  'var(--brand-1)',
  'var(--brand-2)',
  'var(--brand-3)',
  'var(--brand-4)',
  'var(--brand-5)',
  'var(--brand-6)',
];

function BrandMark() {
  return (
    <div className="brand-mark" aria-label="Sales Academy">
      {BRAND_FAN_COLORS.map((c, i) => (
        <span key={i} className="brand-mark__bar" style={{ background: c }} />
      ))}
    </div>
  );
}

/** Phase-A stub for the real learnerStore hook (which depends on the
 *  full types + seed slice that Phase B brings over). Returns a name
 *  to render in the corner pill. */
function useLearner() {
  return { display_name: 'Alex Rivera' };
}

export default function AppShell() {
  const learner = useLearner();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Auto-close the mobile drawer on route change so the user lands cleanly
  // on the new screen instead of seeing the menu still open.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className={`shell ${navOpen ? 'shell--nav-open' : ''}`}>
      <header className="topbar">
        <div className="topbar__left">
          <NavLink to="/" className="topbar__brand" end>
            <BrandMark />
            <span className="wordmark">Sales Academy</span>
          </NavLink>
          <nav
            id="primary-nav"
            className={`topbar__nav ${navOpen ? 'topbar__nav--open' : ''}`}
            aria-label="Primary"
          >
            <NavLink to="/" end className="navlink">
              Home
            </NavLink>
            <NavLink to="/learn" className="navlink">
              Learn
            </NavLink>
            <NavLink to="/practice" className="navlink">
              Practice
            </NavLink>
            <NavLink to="/live-lab" className="navlink">
              Live Lab
            </NavLink>
            <NavLink to="/playbook" className="navlink">
              Playbook
            </NavLink>
            <NavLink to="/team" className="navlink navlink--team">
              Team
              <span className="coming-soon">Manager view</span>
            </NavLink>
          </nav>
        </div>
        <div className="topbar__right">
          <div className="learner-pill">
            <span className="learner-pill__avatar" aria-hidden />
            <span className="learner-pill__name">{learner.display_name}</span>
          </div>
          <button
            type="button"
            className="topbar__menu-toggle"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen}
            aria-controls="primary-nav"
            onClick={() => setNavOpen((v) => !v)}
          >
            <span className="topbar__menu-bar" aria-hidden />
            <span className="topbar__menu-bar" aria-hidden />
            <span className="topbar__menu-bar" aria-hidden />
          </button>
        </div>
      </header>

      {navOpen && (
        <button
          type="button"
          className="topbar__scrim"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
      )}

      <main className="shell__main">
        <Outlet />
      </main>
    </div>
  );
}
